import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import LaptopChromebookRoundedIcon from '@mui/icons-material/LaptopChromebookRounded';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { FeatureCard, FeatureIcon, CardTitle, CardText, accentAt, AccreditationNote } from '../components/ui';

const SITE = 'https://qasc.it';

// The four training areas of the original /formazione hub page.
const areas = [
  {
    icon: <HealthAndSafetyRoundedIcon />,
    title: 'Formazione Sicurezza',
    subtitle: 'Sicurezza e salute sul lavoro',
    text: 'I corsi previsti dal D.Lgs. 81/08 e dagli Accordi Stato-Regioni: lavoratori, figure specializzate, gestione emergenza, addestramento per lavori specifici e attrezzature.',
    to: '/formazione/formazione-sicurezza',
  },
  {
    icon: <LaptopChromebookRoundedIcon />,
    title: 'E-Learning',
    subtitle: 'Formazione per aziende online',
    text: 'La piattaforma implementata con il partner MADE HSE: percorsi fruibili in ogni momento da PC o smartphone, con attestato via e-mail appena terminato il corso.',
    to: '/e-learning',
  },
  {
    icon: <RecyclingRoundedIcon />,
    title: 'Ambiente e Qualità',
    subtitle: 'Corsi di qualifica ambientale',
    text: 'Aggiornamento per Tecnici Competenti in Acustica (TCA) e corso per Responsabile tecnico Albo Gestori Ambientali.',
    to: '/formazione/ambiente-e-qualita',
  },
  {
    icon: <SavingsRoundedIcon />,
    title: 'Finanziata',
    subtitle: 'Cerca i fondi finanziati',
    text: 'Progetti formativi su misura finanziati con fondi interprofessionali, bandi regionali ed europei, gestiti fino alla rendicontazione finale.',
    to: '/formazione-finanziata',
  },
];

function FormazionePage() {
  return (
    <PageLayout
      seo={{
        title: 'Formazione | QAS Consulting',
        description:
          'Noi di QAS riteniamo che la formazione sia l’elemento principale in qualsiasi organizzazione: formazione sicurezza, e-learning, corsi ambiente e qualità e formazione finanziata.',
        canonical: `${SITE}/formazione`,
      }}
      hero={{
        kicker: 'Formazione',
        title: 'Senza formazione non c’è crescita',
        subtitle:
          'Noi di QAS riteniamo che la formazione sia l’elemento principale in qualsiasi organizzazione. Senza la formazione non c’è crescita e senza crescita non c’è futuro.',
      }}
    >
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Le aree formative"
            title="Scegli il tuo percorso"
            subtitle="Quattro aree per costruire la formazione della tua azienda: in aula, presso la tua sede o online."
          />
          <Grid container spacing={4}>
            {areas.map((a, i) => (
              <Grid item xs={12} sm={6} key={a.title}>
                <FeatureCard
                  component={RouterLink}
                  to={a.to}
                  sx={{ display: 'flex', flexDirection: 'column', textDecoration: 'none' }}
                >
                  <FeatureIcon accent={accentAt(i)}>{a.icon}</FeatureIcon>
                  <CardTitle sx={{ mb: 0.5 }}>{a.title}</CardTitle>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      color: '#86a315',
                      mb: 1.5,
                    }}
                  >
                    {a.subtitle}
                  </Typography>
                  <CardText sx={{ flexGrow: 1 }}>{a.text}</CardText>
                  <Typography
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      mt: 2.5,
                      color: 'primary.main',
                      fontFamily: '"Sora", sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      '& svg': { fontSize: '17px' },
                    }}
                  >
                    Scopri di più
                    <ArrowForwardRoundedIcon />
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography
              component={RouterLink}
              to="/corsi-in-evidenza"
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 600,
                fontSize: '15.5px',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Vai ai corsi in evidenza →
            </Typography>
          </Box>
        </Container>
      </Section>
    </PageLayout>
  );
}

export default FormazionePage;
